import React from "react";
import { Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowIcon } from "../icons";
import { type NavItem } from "./types";
import "./NavigationItem.scss";

interface NavigationItemProps {
  item: NavItem;
  activeItem?: string;
  onItemClick?: (label: string) => void;
  onIconClick?: (label: string) => void;
}

const NavigationItem: React.FC<NavigationItemProps> = ({
  item,
  activeItem,
  onItemClick,
  onIconClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if item is active based on current location
  const isActiveByPath = item.path
    ? location.pathname === item.path ||
      location.pathname.startsWith(`${item.path}/`)
    : false;

  const isActive = item.isActive || activeItem === item.label || isActiveByPath;

  const itemClasses = [
    "nav-item",
    item.isNested && "nav-item-nested",
    item.isNestedDeep && "nav-item-nested-deep",
    isActive && "nav-item-active",
    item.isExternal && "nav-item-external",
    item.isBottom && "nav-item-bottom",
  ]
    .filter(Boolean)
    .join(" ");

  const handleClick = () => {
    // External items with path will be handled by <a> tag
    if (item.isExternal && item.path) {
      return; // Let the browser handle navigation via <a> tag
    }

    // If item has a path property, use it for navigation
    if (item.path) {
      // Check if it's an internal route (starts with /home)
      if (item.path.startsWith("/home")) {
        // Remove /home prefix for internal routing
        const internalPath = item.path.replace("/home", "") || "/";
        navigate(internalPath);
      } else {
        // For other paths, just navigate directly
        window.location.href = item.path;
      }
      return;
    }

    // Otherwise call the provided onItemClick handler
    // For external items, this will be handleExternalItemClick from SidebarNavigation
    // For internal items, this will be the app-specific handler
    if (onItemClick) {
      onItemClick(item.label);
    }
  };

  const handleArrowClick = (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent default link behavior
    e.stopPropagation(); // Prevent item click

    // Arrow click opens external apps in new tab
    if (item.isExternal && item.path) {
      window.open(item.path, "_blank");
    }
  };

  // For external items with path, render as an anchor tag
  if (item.isExternal && item.path) {
    return (
      <Box
        component="a"
        href={item.path}
        className={itemClasses}
        sx={{
          cursor: "pointer",
          textDecoration: "none",
          color: "inherit",
          display: "flex",
          alignItems: "center",
          "&:hover": {
            textDecoration: "none",
          },
        }}
      >
        {item.icon}
        <span className="body-m-regular">{item.label}</span>
        <Box
          className="nav-arrow"
          onClick={handleArrowClick}
          sx={{
            display: "inline-flex",
            cursor: "pointer",
            marginLeft: "auto",
          }}
        >
          <ArrowIcon />
        </Box>
      </Box>
    );
  }

  // For non-external items, render as regular div with click handler
  return (
    <Box
      className={itemClasses}
      onClick={handleClick}
      sx={{ cursor: "pointer" }}
    >
      {item.icon}
      <span className="body-m-regular">{item.label}</span>
      {item.isExternal && (
        <Box
          className="nav-arrow"
          onClick={onIconClick ? handleArrowClick : undefined}
          sx={{
            display: "inline-flex",
            cursor: onIconClick ? "pointer" : "inherit",
            marginLeft: "auto",
          }}
        >
          <ArrowIcon />
        </Box>
      )}
    </Box>
  );
};

export default NavigationItem;
